"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";

// Mobile menu toggle component
const MobileMenuToggle = ({ isOpen, toggle }: { isOpen: boolean; toggle: () => void }) => {
  return (
    <button
      className="lg:hidden flex items-center p-2"
      onClick={toggle}
      aria-label={isOpen ? "Close menu" : "Open menu"}
      aria-expanded={isOpen}
    >
      <svg
        className="h-6 w-6 text-gray-700"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        {isOpen ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        )}
      </svg>
    </button>
  );
};

// Dropdown menu component
const DropdownMenu = ({ title, items }: { title: string; items: { name: string; href: string }[] }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative group">
      <button
        className="nav-link flex items-center gap-1 py-2"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        aria-expanded={isOpen}
        aria-label={`Toggle ${title} menu`}
      >
        {title}
        <svg
          className="h-4 w-4 transition-transform duration-200"
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0)" }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        className={`absolute left-0 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 transition-all duration-200 z-10 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        } group-hover:opacity-100 group-hover:visible`}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <div className="py-1" role="menu" aria-orientation="vertical">
          {items.map((item, index) => (
            <Link
              key={index}
              href={item.href}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              role="menuitem"
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// Mobile dropdown menu component
const MobileDropdownMenu = ({ title, items }: { title: string; items: { name: string; href: string }[] }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label={`Toggle ${title} menu`}
      >
        <span>{title}</span>
        <svg
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className={`transition-all duration-200 overflow-hidden ${isOpen ? "max-h-96" : "max-h-0"}`}>
        {items.map((item, index) => (
          <Link
            key={index}
            href={item.href}
            className="block pl-8 pr-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            {item.name}
          </Link>
        ))}
      </div>
    </div>
  );
};

const MainNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const platformItems = [
    { name: "Platform Overview", href: "/platform/overview" },
    { name: "E-commerce Module", href: "/platform/e-commerce-module" },
    { name: "Marketplace Module", href: "/platform/marketplace-module" },
    { name: "Analytics & Insights", href: "/platform/analytics-insights" },
    { name: "Loyalty & Rewards", href: "/platform/loyalty-rewards" },
  ];

  const solutionsItems = [
    { name: "Consumer Brands", href: "/solutions/consumer-brands" },
    { name: "Sport & Entertainment Brands", href: "/solutions/sport-entertainment-brands" },
    { name: "Food & Beverage Brands", href: "/solutions/food-beverage" },
  ];

  const aboutItems = [
    { name: "About Us", href: "/about" },
    { name: "Our Story", href: "/about/our-story" },
    { name: "Team", href: "/about/team" },
  ];

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between h-16">
          {/* Logo and desktop navigation */}
          <div className="flex items-center">
            <Link href="/" className="flex-shrink-0 flex items-center max-[180px]:hidden">
                <div className="relative h-10 w-32">
                  <Image
                    src="/images/logo.png"
                    alt="Perxify logo"
                    fill
                    className="object-contain"
                    priority={true}
                    sizes="128px"
                  />
                </div>
            </Link>


            {/* Desktop navigation */}
            <div className="hidden lg:ml-10 lg:flex lg:items-center lg:space-x-6">
              <DropdownMenu title="Platform" items={platformItems} />
              <DropdownMenu title="Solutions" items={solutionsItems} />
              <Link href="/case-studies/booksshark" className="nav-link py-2">Case Study</Link>
              <DropdownMenu title="About" items={aboutItems} />
              <Link href="/pricing" className="nav-link py-2">Pricing</Link>
              <Link href="/blog" className="nav-link py-2">Blog</Link>
              <Link href="/faq" className="nav-link py-2">FAQ</Link>
            </div>
          </div>

          {/* CTA and mobile menu button */}
          <div className="flex items-center">
            <Link href="/contact/schedule-demo" className="hidden md:block ml-4 btn-primary text-sm max-[371px]:hidden">
              Schedule a Demo
            </Link>
            <Link href="/contact" className="hidden md:block ml-4 text-sm text-gray-700 hover:text-gray-900">
              Contact
            </Link>
            <MobileMenuToggle isOpen={mobileMenuOpen} toggle={() => setMobileMenuOpen(!mobileMenuOpen)} />
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <div
        className={`mobile-menu-overlay ${mobileMenuOpen ? "open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      ></div>

      {/* Mobile menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        <div className="px-2 pt-2 pb-3 space-y-1">
          <MobileDropdownMenu title="Platform" items={platformItems} />
          <MobileDropdownMenu title="Solutions" items={solutionsItems} />
          <Link
            href="/case-studies/booksshark"
            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            Case Study
          </Link>
          <MobileDropdownMenu title="About" items={aboutItems} />
          <Link
            href="/pricing"
            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            Pricing
          </Link>
          <Link
            href="/blog"
            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            Blog
          </Link>
          <Link
            href="/faq"
            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            FAQ
          </Link>
          <Link
            href="/contact"
            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            Contact
          </Link>
          <div className="pt-4 pb-3 border-t border-gray-200">
            <Link
              href="/contact/schedule-demo"
              className="block w-full text-center px-4 py-2 bg-[#00A4BD] text-white rounded-md"
              onClick={() => setMobileMenuOpen(false)}
            >
              Schedule a Demo
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default MainNav;